import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/project/jain_elibrary-admin/components/ui/button";
import { Input } from "@/project/jain_elibrary-admin/components/ui/input";
import { Label } from "@/project/jain_elibrary-admin/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/project/jain_elibrary-admin/components/ui/card";
import { toast } from "sonner";
import { Eye, EyeOff, Library } from "lucide-react";
import { showErrorToast } from "@/utils/errorHandler";
import { adminAPI } from "../services/adminService";
import { paths } from "../utils/paths";
import {
  setAdminAccessToken,
  setAdminRefreshToken,
  setAdminUserInfo,
} from "@lib/storeManager";
import { jwtDecode } from "jwt-decode";

interface DecodedToken {
  exp: number;
  user_id?: number;
  email?: string;
  first_name?: string;
  last_name?: string;
  user_type?: string;
}

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error("Please enter both email and password");
      return;
    }

    setLoading(true);
    try {
      const response = await adminAPI.post("/auth/admin/login/", {
        email,
        password,
      });

      const data = response.data;
      if (data?.access && data?.refresh) {
        setAdminAccessToken(data.access);
        setAdminRefreshToken(data.refresh);

        const decoded = jwtDecode<DecodedToken>(data.access);
        const userInfo = {
          first_name: data.user?.first_name ?? decoded.first_name ?? "",
          last_name: data.user?.last_name ?? decoded.last_name ?? "",
          email: data.user?.email ?? decoded.email ?? email,
          user_type: data.user?.user_type ?? decoded.user_type ?? "",
        };
        setAdminUserInfo(userInfo);
        // Dispatch custom event to notify other components
        window.dispatchEvent(new Event("userInfoUpdated"));

        toast.success("Login successful!");
        navigate(paths.dashboard);
      } else {
        toast.error("Invalid response from server");
      }
    } catch (error: any) {
      console.error("Login error:", error);
      showErrorToast(error, "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-admin-green">
            <Library className="h-8 w-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold">
            JAIN eLibrary Admin
          </CardTitle>
          <CardDescription>
            Enter your credentials to access the admin portal
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" required>
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@example.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                disabled={loading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" required>
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(prev => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Logging in..." : "Login"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
